import React from 'react';
import { Link } from 'react-router-dom';
import { Timeline, TimelineItem } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';
import {
  buildPageStateKey,
  type FilteredUserFlowResults
} from '@deque/axe-devtools-issues-utils';
import styles from './UserFlowResults.css';
import useIssuesFilter, {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';

interface UserFlowResultsProps {
  results: FilteredUserFlowResults;
}

const UserFlowDetails: React.FC<UserFlowResultsProps> = ({ results }) => {
  const { t } = useTranslation();
  const { filters } = useIssuesFilter([]);
  return (
    <div>
      <h3>{t('Recorded User Flow')}</h3>
      <Timeline className={styles.timeline}>
        <TimelineItem
          icon={<span className={styles.startIcon} />}
          className={styles.recordingStarted}
        >
          <strong>{t('Recording started')}</strong>
          <div className={styles.url}>{results.pageStateResults[0].url}</div>
        </TimelineItem>
        {results.pageStateResults.map((pageStateResult, index) => {
          const issueCount = pageStateResult.result.reduce(
            (totalCount, { uniques }) => totalCount + uniques.length,
            0
          );
          const showUrl =
            index !== 0 &&
            pageStateResult.url &&
            pageStateResult.url !== results.pageStateResults[index - 1]?.url;

          const issuesSearchParams = getSearchParamsFromFilters({
            ...getBaseFilters(filters),
            pageStateKey: buildPageStateKey(pageStateResult)
          });
          const pageStateText =
            pageStateResult.page_state ||
            (index === 0 ? t('Initial Page State') : t('Page State Change'));
          const issueCountText =
            issueCount === 1
              ? t('1 issue')
              : t('{{issueCount}} issues', { issueCount });
          const urlDisplayText = showUrl ? ` ${pageStateResult.url}` : '';
          return (
            <TimelineItem key={index}>
              {pageStateText}
              {showUrl && (
                <div className={styles.url}>{pageStateResult.url}</div>
              )}
              <Link
                className="Link"
                to={`${
                  location.pathname
                }/issues?${issuesSearchParams.toString()}`}
                aria-label={`${issueCountText}: ${pageStateText}${urlDisplayText}`}
              >
                {issueCountText}
              </Link>
            </TimelineItem>
          );
        })}
        <TimelineItem icon={<span className={styles.endIcon} />}>
          <strong>{t('Recording stopped')}</strong>
        </TimelineItem>
      </Timeline>
    </div>
  );
};

export default UserFlowDetails;
